import clsx from 'clsx';
import { useCallback, useLayoutEffect, useRef, useState } from 'react';

import * as styles from './node.css';
import type { NavigationPanelRevealRenameFinishReason } from './rename-intent';

export const NavigationPanelTreeNodeInlineRename = ({
  setRenaming,
  handleRename,
  rawName,
  protectInitialFocus = false,
  className,
  inputTestId = 'rename-modal-input',
  onFinish,
}: {
  setRenaming: (renaming: boolean) => void;
  handleRename: (newName: string) => void;
  rawName: string | undefined;
  protectInitialFocus?: boolean;
  className?: string;
  inputTestId?: string;
  onFinish?: (reason: NavigationPanelRevealRenameFinishReason) => void;
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [name, setName] = useState(rawName ?? '');
  const finishedRef = useRef(false);
  const mountedAtRef = useRef(Date.now());

  const focusAndSelect = useCallback(() => {
    const input = inputRef.current;
    if (!input || input.ownerDocument.activeElement === input) return;
    input.focus();
    input.setSelectionRange(0, input.value.length, 'forward');
  }, []);

  useLayoutEffect(() => {
    focusAndSelect();
    const animationFrame = requestAnimationFrame(focusAndSelect);
    const timeouts = (protectInitialFocus ? [220, 500, 800] : [220]).map(
      delay => window.setTimeout(focusAndSelect, delay)
    );
    return () => {
      cancelAnimationFrame(animationFrame);
      timeouts.forEach(clearTimeout);
    };
  }, [focusAndSelect, protectInitialFocus]);

  const finish = useCallback(
    (commit: boolean, reason: NavigationPanelRevealRenameFinishReason) => {
      if (finishedRef.current) return;
      finishedRef.current = true;
      if (commit && name !== (rawName ?? '')) {
        handleRename(name);
      }
      setRenaming(false);
      onFinish?.(reason);
    },
    [handleRename, name, onFinish, rawName, setRenaming]
  );

  return (
    <input
      ref={inputRef}
      className={clsx(styles.inlineRenameInput, className)}
      data-testid={inputTestId}
      value={name}
      onBlur={() => {
        const titleIsUntouched = name === (rawName ?? '');
        const navigationStillSettling =
          Date.now() - mountedAtRef.current < 1000;
        if (
          protectInitialFocus &&
          titleIsUntouched &&
          navigationStillSettling
        ) {
          window.setTimeout(focusAndSelect, 0);
          return;
        }
        finish(true, 'blur');
      }}
      onChange={event => setName(event.target.value)}
      onClick={event => {
        event.preventDefault();
        event.stopPropagation();
      }}
      onDoubleClick={event => event.stopPropagation()}
      onKeyDown={event => {
        if (event.key === 'Enter') {
          event.preventDefault();
          finish(true, 'submit');
        } else if (event.key === 'Escape') {
          event.preventDefault();
          finish(false, 'cancel');
        }
        event.stopPropagation();
      }}
    />
  );
};
